import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { db, setNeonAccessTokenGetter } from "@/integrations/neon/db";
import type { AppAuthSession } from "@/integrations/auth/types";
import {
  authFetchJson,
  logoutRemote,
  refreshTokens,
} from "@/integrations/auth/http";
import { clearStoredTokens, loadStoredTokens } from "@/integrations/auth/tokens";
import { UserProfile, TeamInfo, TeamInfoArray, UserRole } from "./types";
import { toast } from "sonner";

export type { UserProfile, TeamInfo, TeamInfoArray, UserRole };

interface AuthContextProps {
  session: AppAuthSession | null;
  user: AppAuthSession["user"] | null;
  userProfile: UserProfile | null;
  teamInfo: TeamInfoArray | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isTeamMember: boolean;
  isRegularUser: boolean;
  debugMode: boolean;
  setDebugMode: React.Dispatch<React.SetStateAction<boolean>>;
  setSession: React.Dispatch<React.SetStateAction<AppAuthSession | null>>;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  refreshUserProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const defaultAuthContext: AuthContextProps = {
  session: null,
  user: null,
  userProfile: null,
  teamInfo: null,
  isLoading: true,
  isAuthenticated: false,
  isAdmin: false,
  isTeamMember: false,
  isRegularUser: false,
  debugMode: false,
  setDebugMode: () => {},
  setSession: () => {},
  setUserProfile: () => {},
  refreshUserProfile: async () => {},
  signOut: async () => {},
};

export const AuthContext = createContext<AuthContextProps>(defaultAuthContext);

function decodeJwtExp(accessToken: string): number {
  try {
    const part = accessToken.split(".")[1];
    if (!part) return Math.floor(Date.now() / 1000) + 900;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(b64));
    return typeof json.exp === "number"
      ? json.exp
      : Math.floor(Date.now() / 1000) + 900;
  } catch {
    return Math.floor(Date.now() / 1000) + 900;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<AppAuthSession | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [teamInfo, setTeamInfo] = useState<TeamInfoArray | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [debugMode, setDebugMode] = useState(false);
  const isMountedRef = useRef(true);
  const refreshingProfileRef = useRef(false);
  const isLoggingOutRef = useRef(false);
  const sessionRef = useRef<AppAuthSession | null>(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    setNeonAccessTokenGetter(() => sessionRef.current?.access_token ?? null);
  }, []);

  const fetchTeamInfo = useCallback(async (userId: string) => {
    if (!userId) {
      setTeamInfo(null);
      return;
    }
    try {
      const { data: teamData, error: teamError } = await db.rpc(
        "get_user_teams",
        { user_id_param: userId }
      );
      if (teamError || !teamData || (Array.isArray(teamData) && teamData.length === 0)) {
        setTeamInfo(null);
      } else {
        setTeamInfo(teamData as unknown as TeamInfoArray);
      }
    } catch {
      setTeamInfo(null);
    }
  }, []);

  const loadMeWithAccess = useCallback(
    async (
      access: string,
      refresh: string
    ): Promise<{
      user: { id: string; email?: string };
      profile: UserProfile | null;
    } | null> => {
      try {
        return await authFetchJson<{
          user: { id: string; email?: string };
          profile: UserProfile | null;
        }>("/me", { method: "GET", accessToken: access });
      } catch {
        const t = await refreshTokens();
        if (!t) return null;
        return await authFetchJson<{
          user: { id: string; email?: string };
          profile: UserProfile | null;
        }>("/me", { method: "GET", accessToken: t.access_token });
      }
    },
    []
  );

  const refreshUserProfile = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    if (refreshingProfileRef.current) return;
    refreshingProfileRef.current = true;
    setIsLoading(true);
    setProfileError(null);

    try {
      let { access, refresh } = loadStoredTokens();
      if (!access && !refresh) {
        sessionRef.current = null;
        setSession(null);
        setUserProfile(null);
        setTeamInfo(null);
        return;
      }
      if (!access && refresh) {
        const t = await refreshTokens();
        if (!t) {
          clearStoredTokens();
          sessionRef.current = null;
          setSession(null);
          setUserProfile(null);
          setTeamInfo(null);
          return;
        }
        access = t.access_token;
        refresh = t.refresh_token;
      }
      if (!access || !refresh) {
        clearStoredTokens();
        sessionRef.current = null;
        setSession(null);
        setUserProfile(null);
        setTeamInfo(null);
        return;
      }

      const me = await loadMeWithAccess(access, refresh);
      if (!me) {
        clearStoredTokens();
        sessionRef.current = null;
        setSession(null);
        setUserProfile(null);
        setTeamInfo(null);
        return;
      }

      const latest = loadStoredTokens();
      const acc = latest.access ?? access;
      const ref = latest.refresh ?? refresh;

      const nextSession: AppAuthSession = {
        access_token: acc,
        refresh_token: ref,
        expires_at: decodeJwtExp(acc),
        user: { id: me.user.id, email: me.user.email },
      };
      sessionRef.current = nextSession;
      setSession(nextSession);

      if (me.profile) {
        setUserProfile(me.profile);
      } else {
        const { data, error } = await db
          .from("user_profiles")
          .select("*")
          .eq("id", me.user.id)
          .maybeSingle();
        if (!error && data) {
          setUserProfile(data as UserProfile);
        } else {
          setUserProfile(null);
          setProfileError("Profile not found");
        }
      }

      await fetchTeamInfo(me.user.id);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("refreshUserProfile:", msg);
      setProfileError(msg);
      setUserProfile(null);
      setTeamInfo(null);
      toast.error("Failed to load your profile.");
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
      refreshingProfileRef.current = false;
    }
  }, [fetchTeamInfo, loadMeWithAccess]);

  useEffect(() => {
    isMountedRef.current = true;
    void (async () => {
      const { access, refresh } = loadStoredTokens();
      if (access && refresh) {
        await refreshUserProfile();
      } else {
        setIsLoading(false);
      }
    })();
    return () => {
      isMountedRef.current = false;
    };
  }, [refreshUserProfile]);

  useEffect(() => {
    if (!session?.refresh_token) return;
    const id = window.setInterval(async () => {
      const t = await refreshTokens();
      if (t && isMountedRef.current) {
        setSession((prev) =>
          prev
            ? {
                ...prev,
                access_token: t.access_token,
                refresh_token: t.refresh_token,
                expires_at: decodeJwtExp(t.access_token),
              }
            : null
        );
      }
    }, 8 * 60 * 1000);
    return () => clearInterval(id);
  }, [session?.refresh_token]);

  const user = session?.user ?? null;
  const isAdmin = !!userProfile?.role && userProfile.role === "admin";
  const isRegularUser = !!userProfile?.role && userProfile.role === "user";
  const isTeamMember = !!teamInfo && teamInfo.length > 0;

  const signOut = async () => {
    isLoggingOutRef.current = true;
    setIsLoading(true);
    try {
      const rt =
        sessionRef.current?.refresh_token ?? loadStoredTokens().refresh;
      await logoutRemote(rt);
      clearStoredTokens();
      sessionRef.current = null;
      setSession(null);
      setUserProfile(null);
      setTeamInfo(null);
      toast.success("Signed out successfully");
    } catch (err: unknown) {
      console.error(err);
      clearStoredTokens();
      sessionRef.current = null;
      setSession(null);
      setUserProfile(null);
      setTeamInfo(null);
    } finally {
      isLoggingOutRef.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (profileError) {
      toast.error(profileError);
    }
  }, [profileError]);

  const value: AuthContextProps = {
    session,
    user,
    userProfile,
    teamInfo,
    isLoading,
    isAuthenticated: !!(session?.user?.id && session?.access_token),
    isAdmin,
    isTeamMember,
    isRegularUser,
    debugMode,
    setDebugMode,
    setSession,
    setUserProfile,
    refreshUserProfile,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};
